import type { ResourceType } from '@medplum/fhirtypes'
import { invariant } from '@seen/core/invariant'
import { type Schema, tryGetSchema } from '@seen/core/medplum'
import { type UseQueryResult, useQueries } from '@tanstack/react-query'
import { zip } from 'lodash-es'
import { useCallback } from 'react'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import { useErrorToast } from '#src/hooks/useErrorToast.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { useMemoValue } from '#src/hooks/useMemoValue.ts'

export function useSchemas<T extends ResourceType>(
  resourceTypes: T[]
): Pick<
  UseQueryResult<{ [K in T]: Schema<K> }>,
  'data' | 'error' | 'isError' | 'isSuccess'
> {
  const medplum = useMedplum()

  const memoResourceTypes = useMemoValue(resourceTypes)
  const combineResults = useCallback(
    (results: UseQueryResult<Schema<T> | null>[]) => {
      const initialValue = {} as { [K in T]: Schema<K> }
      const data = zip(memoResourceTypes, results).reduce(
        (acc, [resourceType, result]) => {
          if (!resourceType) {
            return acc
          }
          invariant(result, 'Query result is required for schema lookup')
          if (!result.isPending) {
            if (result.data == null) {
              console.error(`schema for ${resourceType} is null`)
            } else {
              acc[resourceType] = result.data
            }
          }
          return acc
        },
        initialValue
      )
      return {
        data:
          Object.keys(data).length !== memoResourceTypes.length
            ? undefined
            : data,
        isSuccess: results.every(({ isSuccess }) => isSuccess),
        isError: results.some(({ isError }) => isError),
        error: results.find(({ error }) => error)?.error ?? null,
      }
    },
    [memoResourceTypes]
  )

  const result = useQueries({
    queries: resourceTypes.map((resourceType) => ({
      queryKey: cacheKeys.schema(resourceType),
      queryFn: async () => {
        await medplum.requestSchema(resourceType)
        return tryGetSchema(resourceType)
      },
    })),
    combine: combineResults,
  })

  useErrorToast(
    result.error &&
      `Error accessing schema from ${resourceTypes.join(', ')}: ${result.error.message ?? 'Unknown error'}`
  )

  const memoResult = useMemoValue(result)
  return memoResult
}
